import { supabase } from "@/integrations/supabase/client";

export const podcastListeningService = {
  async startListeningSession(
    userId: string,
    episodeId: string,
    podcastId: string,
    duration: number
  ) {
    const { data, error } = await supabase
      .from('podcast_listening_sessions')
      .insert({
        user_id: userId,
        episode_id: episodeId,
        podcast_id: podcastId,
        duration_seconds: Math.floor(duration),
        progress_seconds: 0,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateProgress(sessionId: string, currentTime: number) {
    const { error } = await supabase
      .from('podcast_listening_sessions')
      .update({
        progress_seconds: Math.floor(currentTime),
        last_progress_update: new Date().toISOString(),
      })
      .eq('id', sessionId);

    if (error) throw error;
  },

  async endSession(sessionId: string, finalProgress: number) {
    const { error } = await supabase
      .from('podcast_listening_sessions')
      .update({
        progress_seconds: Math.floor(finalProgress),
        ended_at: new Date().toISOString(),
      })
      .eq('id', sessionId);

    if (error) throw error;
  },

  async awardXPForListening(sessionId: string, userId: string) {
    // Get session details
    const { data: session, error: sessionError } = await supabase
      .from('podcast_listening_sessions')
      .select('completion_percentage, xp_awarded, episode_id')
      .eq('id', sessionId)
      .single();

    if (sessionError || !session || session.xp_awarded) {
      return { xpEarned: 0 };
    }

    const completionPercentage = session.completion_percentage || 0;
    let xpEarned = 0;

    // Award XP based on completion milestones
    if (completionPercentage >= 100) {
      xpEarned = 25;
    } else if (completionPercentage >= 75) {
      xpEarned = 15;
    } else if (completionPercentage >= 50) {
      xpEarned = 10;
    } else if (completionPercentage >= 25) {
      xpEarned = 5;
    }

    if (xpEarned > 0) {
      // Call the database function to add XP and log activity
      const { error: xpError } = await supabase.rpc('add_xp_and_log_activity', {
        p_user_id: userId,
        p_amount: xpEarned,
        p_activity_type: 'podcast_listened',
        p_activity_data: {
          episode_id: session.episode_id,
          completion_percentage: completionPercentage,
        },
        p_related_content_type: 'podcast_episodes',
        p_related_content_id: session.episode_id,
      });

      if (xpError) {
        console.error('Error awarding XP:', xpError);
        return { xpEarned: 0 };
      }

      // Mark session as XP awarded
      await supabase
        .from('podcast_listening_sessions')
        .update({ xp_awarded: true })
        .eq('id', sessionId);
    }

    return { xpEarned, completionPercentage };
  },

  async getListeningProgress(userId: string, episodeId: string) {
    const { data, error } = await supabase
      .from('podcast_listening_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('episode_id', episodeId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) return null;
    return data;
  },

  // Log playback events for detailed analytics
  async logPlaybackEvent(
    sessionId: string,
    userId: string,
    episodeId: string,
    eventType: 'play' | 'pause' | 'seek' | 'speed_change' | 'volume_change',
    positionSeconds: number,
    metadata?: Record<string, any>
  ) {
    const { error } = await supabase
      .from('podcast_playback_events')
      .insert({
        session_id: sessionId,
        user_id: userId,
        episode_id: episodeId,
        event_type: eventType,
        position_seconds: Math.floor(positionSeconds),
        metadata: metadata || {},
      });

    if (error) throw error;
  },
};
